import { useMemo } from "react";

import { useCurrentUser } from "@/hooks/current-user";
import { derivePermissions, NO_PERMISSIONS, type Permissions } from "@/lib/roles";

/**
 * The advisory permissions hook: capability booleans for the current session.
 *
 * Advisory means exactly that — it hides nav items and buttons. Python use
 * cases remain the enforcement (#67 §4, §6), so nothing here may be the only
 * thing standing between a user and a write.
 *
 * It reads the one identity query rather than taking roles as an argument, so
 * every gate in the UI answers from the same cache entry the `_authed` gate
 * already populated. Before the query settles — and for a session with no
 * roles — the answer is `NO_PERMISSIONS`: deny-by-default, never `undefined`,
 * so a caller can render a gate without a loading branch of its own.
 */
export function usePermissions(): Permissions {
  const { data } = useCurrentUser();
  const roles = data?.session.roles;
  return useMemo(() => (roles ? derivePermissions(roles) : NO_PERMISSIONS), [roles]);
}
